import {
  useHierarchicalMenu,
  useRange,
  useRefinementList,
  useSearchBox,
  useSortBy,
  useToggleRefinement,
} from "react-instantsearch";

import { type CategoryFacet } from "@/stf.data/schema";

interface VirtualWidgetProps {
  categoryAttribute: CategoryFacet[];
  categoryVariant: CategoryFacet[];
  fixedRange: {
    searchSlug: string;
    urlSlug: string;
  }[];
  hierarchicalMenuAttributes: string[];
  sortByItems: {
    label: string;
    value: string;
    slug: string;
  }[];
}

const VirtualHierarchicalMenu = (
  props: Pick<VirtualWidgetProps, "hierarchicalMenuAttributes">,
) => {
  const { hierarchicalMenuAttributes } = props;

  useHierarchicalMenu({
    attributes: hierarchicalMenuAttributes,
  });

  return null;
};

const VirutalSortBy = (
  props: Pick<VirtualWidgetProps, "sortByItems">,
) => {
  const { sortByItems } = props;

  useSortBy({
    items: sortByItems,
  });

  return null;
};

const VirtualSearchBox = () => {
  useSearchBox();

  return null;
};

interface VirtualRefinementListItemProps {
  attribute: string;
}
const VirtualRefinementListItem = (props: VirtualRefinementListItemProps) => {
  const { attribute } = props;

  useRefinementList({ attribute });

  return null;
};

interface VirtualRangeItemProps {
  attribute: string;
}
const VirtualRangeItem = (props: VirtualRangeItemProps) => {
  const { attribute } = props;

  useRange({ attribute });

  return null;
};

interface VirtualToggleRefinementProps {
  attribute: string;
}
const VirtualToggleRefinement = (props: VirtualToggleRefinementProps) => {
  const { attribute } = props;

  useToggleRefinement({ attribute });

  return null;
};

const VirtualFilteralbeOptions = (
  props: Pick<VirtualWidgetProps, "categoryAttribute" | "categoryVariant" | "fixedRange">,
) => {
  const { categoryAttribute, categoryVariant, fixedRange } = props;

  return (
    <>
      <VirtualRefinementListItem
        attribute="shopDetail.shopSlug"
      />
      <VirtualToggleRefinement
        attribute="flags.cafPick"
      />
      {categoryAttribute.map((c) => {
        if (!c.filterable) {
          return null;
        }
        if (c.facetType === "rangeSlider") {
          return (
            <VirtualRangeItem
              key={c.slug}
              attribute={`productAttributeDict.${c.label}`}
            />
          );
        }
        if (c.facetType === "refinementList") {
          return (
            <VirtualRefinementListItem
              key={c.slug}
              attribute={`productAttributeDict.${c.label}`}
            />
          );
        }
        return null;
      })}
      {categoryVariant.map((c) => {
        if (!c.filterable) {
          return null;
        }
        if (c.facetType === "rangeSlider") {
          return (
            <VirtualRangeItem
              key={c.slug}
              attribute={`variantSku.variantDisplay.${c.label}.meta.unitData.value`}
            />
          );
        }
        if (c.facetType === "toggleRefinement") {
          return (
            <VirtualToggleRefinement
              key={c.slug}
              attribute={`variantSku.variantDisplay.${c.label}.meta.booleanText.value`}
            />
          );
        }
        if (c.facetType === "refinementList") {
          return (
            <VirtualRefinementListItem
              key={c.slug}
              attribute={`variantSku.variantDisplay.${c.label}`}
            />
          );
        }
        return null;
      })}
      {fixedRange.map((c) => {
        return (
          <VirtualRangeItem
            key={c.searchSlug}
            attribute={c.searchSlug}
          />
        );
      })}
    </>
  );
};

/**
 * Virtual component is needed for instant search to keep the state.
 * Notes:
 * - Each "useSomething" needs its own component (We can combine multiple useSomething into 1 component)
 */

const VirtualWidget = (p: VirtualWidgetProps) => {
  return (
    <>
      <VirtualHierarchicalMenu
        hierarchicalMenuAttributes={p.hierarchicalMenuAttributes}
      />
      <VirutalSortBy
        sortByItems={p.sortByItems}
      />
      <VirtualSearchBox />
      <VirtualFilteralbeOptions
        categoryAttribute={p.categoryAttribute}
        categoryVariant={p.categoryVariant}
        fixedRange={p.fixedRange}
      />
    </>
  );
};

export { VirtualWidget };
